import { Component, DestroyRef, signal, inject, HostListener } from '@angular/core';
import { NavigationCancel, NavigationEnd, NavigationError, NavigationSkipped, NavigationStart, Router, RouterOutlet } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Location } from '@angular/common';
import { LoggerService } from './core/logging/logger.service';

@Component({
  selector: 'app-root',
  imports: [RouterOutlet],
  templateUrl: './app.html',
  styleUrl: './app.css'
})
export class App {

    private location = inject(Location);
    private logger = inject(LoggerService);
    private readonly router = inject(Router);
    private readonly destroyRef = inject(DestroyRef);
    protected readonly title = signal('DRX TestDataPlatform');

    ngOnInit(): void {
      this.logger.info('App', 'Application initialized');
      this.router.events.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(event => {
        if (event instanceof NavigationStart) {
          this.logger.debug('App', 'Navigation started', { id: event.id, trigger: event.navigationTrigger });
        } else if (event instanceof NavigationEnd) {
          const paths: string[] = [];
          let route = this.router.routerState.snapshot.root;
          while (route.firstChild) {
            route = route.firstChild;
            if (route.routeConfig?.path) paths.push(route.routeConfig.path);
          }
          this.logger.info('App', 'Navigation completed', { id: event.id, route: paths.join('/') });
        } else if (event instanceof NavigationError) {
          this.logger.error('App', 'Navigation failed', event.error);
        } else if (event instanceof NavigationCancel || event instanceof NavigationSkipped) {
          this.logger.debug('App', 'Navigation cancelled or skipped', { id: event.id, code: event.code });
        }
      });
    }

    @HostListener('document:keydown', ['$event'])
    onKeydown(event: KeyboardEvent): void {

      if (event.key !== 'Backspace') {
        return;
      }
      
      const target = event.target as HTMLElement;

      if (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable
      ) {
        this.logger.debug('App', 'Backspace ignored - inside editable element');
        return;
      }

      event.preventDefault();
      this.logger.debug('App', 'Back navigation requested with Backspace');
      this.location.back();
    }

}
